// Clinic Service - Centralized clinic data management and utilities
import { apiManager } from './apiManager';

// Clinic setup data API endpoint constant
export const CLINIC_SETUP_DATA_ENDPOINT = '/api/v1/clinic-setup-data';

// Shared data store to prevent duplicate API calls across multiple hook instances
let sharedData = null;
let sharedLoading = true;
let sharedError = null;
let dataPromise = null;
let sharedRefreshCounter = 0; // Shared refresh counter to trigger re-fetch across all instances

class ClinicService {
  constructor() {
    // Service is a singleton, state is managed via module-level variables
  }

  // ============================================================================
  // Shared State Management
  // ============================================================================

  /**
   * Get shared clinic data
   */
  getSharedData() {
    return sharedData;
  }

  /**
   * Set shared clinic data
   */
  setSharedData(data) {
    sharedData = data;
  }

  /**
   * Get shared loading state
   */
  getSharedLoading() {
    return sharedLoading;
  }

  /**
   * Set shared loading state
   */
  setSharedLoading(loading) {
    sharedLoading = loading;
  }

  /**
   * Get shared error state
   */
  getSharedError() {
    return sharedError;
  }

  /**
   * Set shared error state
   */
  setSharedError(error) {
    sharedError = error;
  }

  /**
   * Get shared data promise
   */
  getDataPromise() {
    return dataPromise;
  }

  /**
   * Set shared data promise
   */
  setDataPromise(promise) {
    dataPromise = promise;
  }

  /**
   * Get shared refresh counter
   */
  getRefreshCounter() {
    return sharedRefreshCounter;
  }

  /**
   * Increment refresh counter and dispatch event
   */
  incrementRefreshCounter() {
    sharedRefreshCounter += 1;
    window.dispatchEvent(new Event('clinicDataRefresh'));
    return sharedRefreshCounter;
  }

  // ============================================================================
  // localStorage Management
  // ============================================================================

  /**
   * Get clinic_id from user_data in localStorage
   */
  getClinicIdFromUserData() {
    try {
      const userData = localStorage.getItem("user_data");
      if (userData && userData !== "undefined" && userData !== "null") {
        const userDataParsed = JSON.parse(userData);
        return userDataParsed.clinic_id || null;
      }
    } catch (e) {
      console.error('Error parsing localStorage for clinic_id:', e);
    }
    return null;
  }

  /**
   * Save owned clinics to localStorage
   */
  saveOwnedClinics(clinics) {
    try {
      if (clinics && Array.isArray(clinics)) {
        localStorage.setItem('owned_clinics', JSON.stringify(clinics));
        // No need to save selected_clinic_id - it will be in URL
      }
    } catch (e) {
      console.error('Error saving owned_clinics to localStorage:', e);
    }
  }

  /**
   * Get owned clinics from localStorage
   */
  getOwnedClinicsFromStorage() {
    try {
      const cached = localStorage.getItem('owned_clinics');
      if (cached && cached !== 'undefined' && cached !== 'null') {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error('Error reading owned_clinics from localStorage:', e);
    }
    return [];
  }

  /**
   * Save selected clinic ID to localStorage (DEPRECATED - use URL params instead)
   * @deprecated Use URL params for clinicId instead
   */
  saveSelectedClinicId(clinicId) {
    // No-op: clinicId should be in URL params
  }

  /**
   * Get selected clinic ID from localStorage (DEPRECATED - use URL params instead)
   * @deprecated Use URL params for clinicId instead
   */
  getSelectedClinicId() {
    // Return null - clinicId should come from URL params
    return null;
  }

  /**
   * Save registered clinic ID (fallback when no owned clinics)
   */
  saveRegisteredClinicId(clinicId) {
    if (clinicId !== null && clinicId !== undefined) {
      this.saveSelectedClinicId(clinicId);
    }
  }

  // ============================================================================
  // Data Getter Utilities
  // ============================================================================

  /**
   * Get clinic info from data
   */
  getClinicInfo(data) {
    return data?.clinic || null;
  }

  /**
   * Get clinic types from data
   */
  getClinicTypes(data) {
    return data?.clinic_types || [];
  }

  /**
   * Get countries from data
   */
  getCountries(data) {
    return data?.countries || [];
  }

  /**
   * Get clinic services from data
   */
  getClinicServices(data) {
    return data?.clinic_service_list || [];
  }

  /**
   * Get user location from data
   */
  getUserLocation(data) {
    return data?.user_location || null;
  }

  /**
   * Get clinic members from data
   */
  getClinicMembers(data) {
    return data?.clinic_members || [];
  }

  /**
   * Get owned clinics from data with localStorage fallback
   */
  getOwnedClinics(data) {
    // Try to get from current data first
    if (data?.owned_clinics && Array.isArray(data.owned_clinics)) {
      return data.owned_clinics;
    }
    // Fallback to localStorage
    return this.getOwnedClinicsFromStorage();
  }

  /**
   * Get clinic ID from data (DEPRECATED - use URL params instead)
   * @deprecated Use URL params for clinicId instead
   */
  getClinicId() {
    // Return null - clinicId should come from URL params
    return null;
  }

  /**
   * Check if user is owner of a clinic (by clinicId)
   * @param {number} clinicId - The clinic ID to check (should come from URL params)
   */
  isOwner(clinicId = null) {
    if (!clinicId) return false;
    
    const clinics = this.getOwnedClinicsFromStorage();
    if (!clinics || !Array.isArray(clinics)) return false;
    return clinics.some(c => c.id == clinicId) || false;
  }

  /**
   * Check if user is member of a clinic
   */
  isMember(data) {
    const clinicInfo = this.getClinicInfo(data);
    return clinicInfo?.clinic?.is_member || false;
  }

  // ============================================================================
  // Data Validation and Transformation
  // ============================================================================

  /**
   * Validate clinic data structure
   */
  validateClinicData(data) {
    if (!data || typeof data !== 'object') {
      return false;
    }
    // Basic validation - data should be an object
    return true;
  }

  /**
   * Transform clinic setup API response
   */
  transformClinicSetupResponse(response) {
    if (response?.data?.status === 'success' && response?.data?.data) {
      const responseData = response.data.data;
      
      // Save owned_clinics to localStorage if it exists
      if (responseData?.owned_clinics && Array.isArray(responseData.owned_clinics)) {
        this.saveOwnedClinics(responseData.owned_clinics);
      } else if (responseData?.registered_clinic_id) {
        // Fallback to registered_clinic_id
        this.saveRegisteredClinicId(responseData.registered_clinic_id);
      }
      
      return responseData;
    }
    throw new Error(response?.data?.message || 'Failed to fetch clinic data');
  }

  /**
   * Check if current data matches the requested clinic_id
   */
  dataMatchesClinicId(data, clinicId) {
    if (!data) return false;
    const currentDataClinicId = data?.clinic?.id || null;
    return currentDataClinicId === clinicId || (!clinicId && !currentDataClinicId);
  }

  /**
   * Create endpoint key for API manager caching
   */
  createEndpointKey(endpoint, clinicId) {
    return clinicId ? `${endpoint}?clinic_id=${clinicId}` : endpoint;
  }

  // ============================================================================
  // Cache Management
  // ============================================================================

  /**
   * Reset all shared clinic data
   * Also increments refresh counter to trigger re-fetch in hooks
   */
  resetClinicData() {
    sharedData = null;
    sharedLoading = true;
    sharedError = null;
    dataPromise = null;
    // Reset all endpoint variations (including those with query params)
    apiManager.resetCallVariations(CLINIC_SETUP_DATA_ENDPOINT);
    // Increment refresh counter to trigger re-fetch in all hook instances
    this.incrementRefreshCounter();
  }

  /**
   * Clear shared data if clinic_id changed
   */
  clearDataIfClinicChanged(clinicId) {
    if (sharedData) {
      const currentDataClinicId = sharedData?.clinic?.id || null;
      if (currentDataClinicId !== clinicId && !(clinicId === null && currentDataClinicId === null)) {
        // Clear shared data and reset API manager to allow fetching new clinic data
        apiManager.resetCallVariations(CLINIC_SETUP_DATA_ENDPOINT);
        sharedData = null;
        dataPromise = null;
      }
    }
  }
}

// Create singleton instance
const clinicService = new ClinicService();

export default clinicService;
export { clinicService };

